import { defaultRangeExtractor, useVirtualizer } from "@tanstack/react-virtual";
import {
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]';

const getFocusableElements = (row: HTMLElement) =>
  Array.from(row.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (element) => element.getClientRects().length > 0,
  );

export function VirtualMatterFolderList<T extends { matterId: string }>({
  items,
  expandedMatterIds,
  scrollElement,
  renderItem,
}: {
  items: T[];
  expandedMatterIds: Set<string>;
  scrollElement: HTMLDivElement | null;
  renderItem: (item: T) => ReactNode;
}) {
  const listRef = useRef<HTMLDivElement>(null);
  const [scrollMargin, setScrollMargin] = useState(0);
  const [focusedMatterId, setFocusedMatterId] = useState<string | null>(null);
  const [pendingFocus, setPendingFocus] = useState<{
    matterId: string;
    last: boolean;
  } | null>(null);
  const itemIndexes = useMemo(
    () => new Map(items.map((item, index) => [item.matterId, index])),
    [items],
  );
  const expandedIndexes = useMemo(
    () =>
      items.flatMap((item, index) =>
        expandedMatterIds.has(item.matterId) ? [index] : [],
      ),
    [expandedMatterIds, items],
  );
  const getItemKey = useCallback(
    (index: number) => items[index].matterId,
    [items],
  );
  const rangeExtractor = useCallback(
    (range: Parameters<typeof defaultRangeExtractor>[0]) => {
      // Open repositories retain nested expansion and drag state off screen.
      const indexes = new Set([
        ...defaultRangeExtractor(range),
        ...expandedIndexes,
      ]);
      for (const matterId of [focusedMatterId, pendingFocus?.matterId]) {
        const index = matterId ? itemIndexes.get(matterId) : undefined;
        if (index !== undefined) indexes.add(index);
      }
      return Array.from(indexes).sort((left, right) => left - right);
    },
    [expandedIndexes, focusedMatterId, itemIndexes, pendingFocus?.matterId],
  );
  const virtualizer = useVirtualizer({
    count: items.length,
    getItemKey,
    getScrollElement: () => scrollElement,
    // New sections must attach at the shared scrollbar's current position.
    initialOffset: () => scrollElement?.scrollTop ?? 0,
    estimateSize: () => 36,
    overscan: 6,
    scrollMargin,
    rangeExtractor,
    enabled: Boolean(scrollElement),
  });
  const virtualItems = virtualizer.getVirtualItems();

  const updateScrollMargin = useCallback(() => {
    if (!listRef.current || !scrollElement) return;
    const offset = Math.max(
      0,
      Math.round(
        listRef.current.getBoundingClientRect().top -
          scrollElement.getBoundingClientRect().top +
          scrollElement.scrollTop -
          scrollElement.clientTop,
      ),
    );
    setScrollMargin((previous) => (previous === offset ? previous : offset));
  }, [scrollElement]);

  // Other sections share this scrollbar and can change the list's position.
  useLayoutEffect(updateScrollMargin);
  useLayoutEffect(() => {
    if (!scrollElement || !listRef.current) return;
    const observer = new ResizeObserver(updateScrollMargin);
    observer.observe(listRef.current);
    observer.observe(scrollElement.firstElementChild ?? scrollElement);
    return () => {
      observer.disconnect();
    };
  }, [scrollElement, updateScrollMargin]);

  useLayoutEffect(() => {
    if (!pendingFocus) return;
    const index = itemIndexes.get(pendingFocus.matterId);
    const row = listRef.current?.querySelector<HTMLElement>(
      `[data-index="${index}"]`,
    );
    if (!row) return;
    const targets = getFocusableElements(row);
    const target = pendingFocus.last ? targets.at(-1) : targets[0];
    target?.focus({ preventScroll: true });
    setPendingFocus(null);
  }, [itemIndexes, pendingFocus, virtualItems]);

  const handleRowKeyDown = (
    event: KeyboardEvent<HTMLDivElement>,
    index: number,
  ) => {
    if (event.key !== "Tab" || event.altKey || event.ctrlKey || event.metaKey)
      return;
    const targets = getFocusableElements(event.currentTarget);
    const boundary = event.shiftKey ? targets[0] : targets.at(-1);
    if (event.target !== boundary) return;
    const nextIndex = index + (event.shiftKey ? -1 : 1);
    if (nextIndex < 0 || nextIndex >= items.length) return;
    const nextItem = items[nextIndex];
    event.preventDefault();
    setPendingFocus({ matterId: nextItem.matterId, last: event.shiftKey });
    virtualizer.scrollToIndex(nextIndex, { align: "auto" });
  };

  return (
    <div
      ref={listRef}
      role="list"
      aria-label="Matter folders"
      className="relative"
      style={{ height: virtualizer.getTotalSize() }}
    >
      {virtualItems.map((virtualItem) => {
        const item = items[virtualItem.index];
        return (
          <div
            key={virtualItem.key}
            ref={virtualizer.measureElement}
            data-index={virtualItem.index}
            role="listitem"
            aria-posinset={virtualItem.index + 1}
            aria-setsize={items.length}
            className="absolute top-0 left-0 w-full"
            style={{
              transform: `translateY(${virtualItem.start - scrollMargin}px)`,
            }}
            onFocusCapture={() => {
              setFocusedMatterId(item.matterId);
            }}
            onBlurCapture={(event) => {
              if (
                !listRef.current?.contains(event.relatedTarget as Node | null)
              )
                setFocusedMatterId(null);
            }}
            onKeyDownCapture={(event) => {
              handleRowKeyDown(event, virtualItem.index);
            }}
          >
            {renderItem(item)}
          </div>
        );
      })}
    </div>
  );
}
